import { useCallback, useEffect, useRef, useState } from 'react'
import useCalendar from '../useCalendar'
import { DEFAULT_MAX_MONTH_INDEX } from '../constants/dates'
import getMonthsInView from '../utils/getMonthsInView'

function useScroll() {
  const {
    maxMonthIndex,
    monthIndex,
    monthsLength = DEFAULT_MAX_MONTH_INDEX,
    monthsToShow,
    monthWidth,
    startMonth,
    onDisplayMonthsChange,
    setMonthIndex,
    setTabClose,
  } = useCalendar()

  const scrollBoxDOMNode = useRef<HTMLDivElement>()
  const scrollContainerDOMNode = useRef<HTMLDivElement>()

  const [showLeftArrow, setShowLeftArrow] = useState(monthIndex > 0)
  const [showRightArrow, setShowRightArrow] = useState(monthIndex + monthsToShow < monthsLength)

  const dispatchMonthsInView = useCallback(() => {
    if (!scrollBoxDOMNode || !scrollBoxDOMNode.current) {
      return
    }

    const calendarWrapper = scrollBoxDOMNode.current.children[0] || null

    const monthsInView = getMonthsInView(startMonth, calendarWrapper, scrollContainerDOMNode.current)
    onDisplayMonthsChange(monthsInView)
  }, [scrollBoxDOMNode, scrollContainerDOMNode])

  const onArrowFocus = useCallback(() => {
    setTabClose(true)
  }, [])

  const scrollCalendar = useCallback(() => {
    if (scrollContainerDOMNode?.current) {
      scrollContainerDOMNode.current.scrollLeft = monthIndex * monthWidth
    }
  }, [scrollContainerDOMNode, monthIndex, monthWidth])

  const scrollLeft = useCallback(
    (evt?: Event) => {
      evt?.preventDefault()

      setMonthIndex((prev) => prev - 1)
      setShowLeftArrow(monthIndex > 1)
      setShowRightArrow(true)
    },
    [monthIndex, dispatchMonthsInView]
  )

  const scrollRight = useCallback(
    (evt?: Event) => {
      evt?.preventDefault()
      setMonthIndex((prev) => prev + 1)
      setShowLeftArrow(true)
      setShowRightArrow(monthIndex < maxMonthIndex - monthsToShow + 1)
    },
    [monthIndex, maxMonthIndex, dispatchMonthsInView]
  )

  useEffect(() => {
    scrollCalendar()
    dispatchMonthsInView()
  }, [monthIndex])

  return {
    monthIndex,
    scrollBoxDOMNode,
    scrollContainerDOMNode,
    showLeftArrow,
    showRightArrow,
    onArrowFocus,
    scrollLeft,
    scrollRight,
  }
}

export default useScroll
